import type { ReactNode } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { buttonVariants } from './Button';

/**
 * A round mark in the middle of a picture (a video's play, a file's kind), in the look of every
 * control on a picture (`Button`'s `overlay`). Not a control: the picture under it takes the press.
 */
export function MediaBadge({
  size = 'md',
  children
}: {
  size?: 'sm' | 'md';
  /** An icon. */
  children: ReactNode;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        buttonVariants({
          variant: 'overlay',
          size: size === 'sm' ? 'xs' : 'md',
          layout: 'icon',
          shape: 'rounded'
        }),
        'pointer-events-none absolute top-1/2 left-1/2 -translate-1/2',
        size === 'sm' ? '[&_svg]:size-3.5' : '[&_svg]:size-5'
      )}
    >
      {children}
    </span>
  );
}

/**
 * Over the last picture of a strip or a tile: the whole picture dims in the dark glass of every
 * control on a picture and says "+N" for the `count` pictures not shown (past 99, "99+"); nothing
 * when none are left. The number grows with the picture. `label` ("12 more pictures") is what a
 * screen reader hears; under a picture named with `aria-label` it isn't read, so that name has to
 * say it too.
 */
export function MediaMoreOverlay({
  count,
  label
}: {
  count: number;
  label: string;
}) {
  if (count <= 0) return null;
  return (
    <span
      role="img"
      aria-label={label}
      className="pointer-events-none absolute inset-0 grid place-items-center bg-[rgb(17_24_39/0.55)] font-semibold text-white backdrop-blur-[2px] @container"
    >
      <span
        aria-hidden
        className="text-xs @min-[64px]:text-sm @min-[120px]:text-lg @min-[200px]:text-2xl"
      >
        {count > 99 ? '99+' : `+${count}`}
      </span>
    </span>
  );
}
